import { UseFormReturn } from 'react-hook-form'
import { QueryClient } from 'react-query'

import { createEmptyShippingConfigs } from 'app/components/forms/ShippingReturnRulesForm/helpers/shippingReturnRules.helpers'
import { type ErrorHandlerType } from 'app/services/ErrorHandler'

import { SHIPPING_STATUS_TYPE } from '../constants/status.constants'

import { transformAddressToDTO } from './transforms.helper'

export const generateRequestAddress = async ({
  values,
  callback
}: {
  values: Address
  callback: (dataDto) => Promise<Address>
}) => {
  const isRequest = values && !values?.full_address

  const valuesDto = transformAddressToDTO(values)

  if (isRequest) {
    const resp = await callback({ ...valuesDto })

    return {
      address: resp?.full_address || ''
    }
  } else {
    return {
      address: values?.full_address || ''
    }
  }
}

export const extractListingFormErrors = (errors: ErrorHandlerType) => {
  const auctionFields = ['start_price', 'reserve_price', 'duration_days']
  const fixedPriceFields = ['item_price', 'received_price', 'min_offer_price']

  const transformErrors = Object.entries(errors || {}).reduce(
    (acc, [key, message]) => {
      if (auctionFields.includes(key)) {
        return { ...acc, [`auction.${key}`]: message }
      } else if (fixedPriceFields.includes(key)) {
        return { ...acc, [`fixed_price.${key}`]: message }
      } else {
        return { ...acc, [key]: message }
      }
    },
    {}
  ) as Record<string, string>

  return transformErrors
}

export const removeShippingTypeAndCarrier = ({
  hasCarrier,
  formContext
}: {
  hasCarrier: boolean
  formContext: UseFormReturn<ShippingFV>
}) => {
  formContext.setValue('shipping_types', [])

  if (hasCarrier) {
    formContext.setValue('shipping_variant', '')
    formContext.setValue('shipping_configs', createEmptyShippingConfigs())
  }
}

export const isShippingRestrictionsError = (
  error: ErrorHandlerType
): error is ErrorHandlerType & {
  errors: { _shipping_restrictions: string[] }
} =>
  !!error.errors?._shipping_restrictions &&
  !!error.errors?._shipping_restrictions?.length

export const isCollectShippingType = (shippingTypes: string[]) =>
  !!shippingTypes &&
  shippingTypes.length === 1 &&
  shippingTypes.includes(SHIPPING_STATUS_TYPE.click_and_collect)

export const isShipOnlyShippingType = (shippingTypes: string[]) =>
  !!shippingTypes &&
  shippingTypes.length === 1 &&
  shippingTypes.includes(SHIPPING_STATUS_TYPE.ship_only)

export const isBothShippingType = (shippingTypes?: string[]) => {
  if (!shippingTypes?.length) return false

  const shippingBothRequiredOptions = [
    SHIPPING_STATUS_TYPE.ship_only,
    SHIPPING_STATUS_TYPE.click_and_collect
  ]

  const hasBoth = shippingBothRequiredOptions.every((option) =>
    shippingTypes.includes(option)
  )

  return hasBoth
}

export const hasShipOnlyShippingType = (shippingTypes?: string[]) =>
  !!shippingTypes?.includes(SHIPPING_STATUS_TYPE.ship_only)

export const hasCollectShippingType = (shippingTypes?: string[]) =>
  !!shippingTypes?.includes(SHIPPING_STATUS_TYPE.click_and_collect)

export const hasValueInAddress = (
  address?: AddressFV | Address | null
): address is AddressFV => !!address && 'value' in address

interface CheckIsBlockedShippingProps {
  methods: UseFormReturn<ListingFV & ShippingFV & PricingListingFV>
  queryClient: QueryClient
}

export const checkIsBlockedShipping = ({
  methods,
  queryClient
}: CheckIsBlockedShippingProps) => {
  const [category, subCategoryId, isBlockShipping] = methods.getValues([
    'category',
    'sub_category_id',
    'isBlockShipping'
  ])

  if (!category || !subCategoryId || isBlockShipping) return

  const subCategories = queryClient.getQueryData<SubCategory[]>([
    'options',
    [category]
  ])
  const isDangerSubcategory = !!subCategories?.find(
    (item) => String(item.id) === subCategoryId
  )?.danger

  if (!isDangerSubcategory) return

  methods.setValue('isBlockShipping', isDangerSubcategory)
  methods.setValue('shipping_types', [])
}

export const shippingCarrierSelectedFirstSort =
  (selectedId: string) => (a: ShippingCarrier, b: ShippingCarrier) =>
    Number(b.carrier_id === selectedId) - Number(a.carrier_id === selectedId)
